/**
 * Insurance Stats Component
 * Dashboard showing queue statistics (Admin/Executive only)
 */

import React from 'react';
import { InsuranceStats as StatsType } from '../../types/insurance';

interface InsuranceStatsProps {
  stats: StatsType | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export const InsuranceStats: React.FC<InsuranceStatsProps> = ({
  stats,
  isLoading,
  error,
  onRefresh,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        <p className="font-medium">Failed to load statistics</p>
        <p className="text-sm mt-1">{error}</p>
        <button
          onClick={onRefresh}
          className="mt-2 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-gray-500 py-8">
        No statistics available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Unclaimed"
          value={stats.totalUnclaimed}
          color="blue"
          description="Ready to claim"
        />
        <StatCard
          label="Needs Review"
          value={stats.totalNeedsReview}
          color="yellow"
          description="Manual entry required"
        />
        <StatCard
          label="In Progress"
          value={stats.totalClaimed}
          color="purple"
          description="Currently being worked"
        />
        <StatCard
          label="Completed Today"
          value={stats.totalCompletedToday}
          color="green"
          description="Finished today"
        />
        <StatCard
          label="Discarded Today"
          value={stats.totalDiscardedToday}
          color="gray"
          description="Discarded today"
        />
      </div>

      {/* By Location */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">By Location</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unclaimed
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  In Progress
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Active
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stats.byLocation.map((loc) => (
                <tr key={loc.locationId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {loc.locationName}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">
                    {loc.unclaimed}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-purple-600 font-medium">
                    {loc.claimed}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium">
                    {loc.unclaimed + loc.claimed}
                  </td>
                </tr>
              ))}
              {stats.byLocation.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    No location data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agent Workload (if available) */}
      {stats.agentWorkload && stats.agentWorkload.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Workload</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active Claims
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed Today
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.agentWorkload.map((agent) => (
                  <tr key={agent.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {agent.userName}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-purple-600 font-medium">
                      {agent.claimedCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                      {agent.completedToday}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
        >
          Refresh Stats
        </button>
      </div>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  color: 'blue' | 'yellow' | 'purple' | 'green' | 'gray';
  description: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, description }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <p className="text-sm font-medium opacity-80">{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-60 mt-1">{description}</p>
    </div>
  );
};

export default InsuranceStats;
